import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface ConflictPreferences {
  defaultAction: 'preserve_manual' | 'accept_system' | 'preserve_unless_better';
  /** Always summary — not user-configurable (DB column kept for compatibility). */
  notificationLevel: 'summary';
  measureSpecificOverrides: Record<string, string>;
}

interface DisplayPreferences {
  showPaymentAdjustment: boolean;
}

interface PreferencesContextType {
  conflictPreferences: ConflictPreferences | null;
  displayPreferences: DisplayPreferences;
  loading: boolean;
  loadPreferences: () => Promise<void>;
  updatePreferences: (prefs: ConflictPreferences) => Promise<void>;
  updateDisplayPreferences: (prefs: Partial<DisplayPreferences>) => Promise<void>;
  getPreferenceForMeasure: (measureId: string) => string;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

interface PreferencesProviderProps {
  children: ReactNode;
}

const DEFAULT_DISPLAY_PREFERENCES: DisplayPreferences = {
  showPaymentAdjustment: true,
};

export function PreferencesProvider({ children }: PreferencesProviderProps) {
  const [conflictPreferences, setConflictPreferences] = useState<ConflictPreferences | null>(null);
  const [displayPreferences, setDisplayPreferences] = useState<DisplayPreferences>(DEFAULT_DISPLAY_PREFERENCES);
  const [loading, setLoading] = useState(true);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Load preferences from API
  const loadPreferences = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Load both conflict and display preferences in parallel
      const [conflictResponse, displayResponse] = await Promise.all([
        fetch(`${apiUrl}/api/user/conflict-preferences`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${apiUrl}/api/user/display-preferences`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (conflictResponse.ok) {
        const data = await conflictResponse.json();
        setConflictPreferences({
          ...data,
          notificationLevel: 'summary',
        });
      } else {
        console.error('Failed to load conflict preferences');
        setConflictPreferences({
          defaultAction: 'preserve_unless_better',
          notificationLevel: 'summary',
          measureSpecificOverrides: {}
        });
      }

      if (displayResponse.ok) {
        const data = await displayResponse.json();
        setDisplayPreferences(data);
      } else {
        // Use defaults if API fails or no preferences exist
        setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      // Set defaults on error
      setConflictPreferences({
        defaultAction: 'preserve_unless_better',
        notificationLevel: 'summary',
        measureSpecificOverrides: {}
      });
      setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
    } finally {
      setLoading(false);
    }
  };

  // Update preferences and save to API
  const updatePreferences = async (prefs: ConflictPreferences) => {
    const toSave: ConflictPreferences = { ...prefs, notificationLevel: 'summary' };
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${apiUrl}/api/user/conflict-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(toSave),
      });

      if (response.ok) {
        setConflictPreferences(toSave);
        return;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };

  // Update display preferences and save to API
  const updateDisplayPreferences = async (prefs: Partial<DisplayPreferences>) => {
    const newPrefs = { ...displayPreferences, ...prefs };
    
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${apiUrl}/api/user/display-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newPrefs),
      });

      if (response.ok) {
        setDisplayPreferences(newPrefs);
        return;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save display preferences');
      }
    } catch (error) {
      console.error('Error updating display preferences:', error);
      throw error;
    }
  };

  // Get preference for specific measure (with override support)
  const getPreferenceForMeasure = (measureId: string): string => {
    if (!conflictPreferences) return 'preserve_unless_better';
    
    // Check for measure-specific override first
    const override = conflictPreferences.measureSpecificOverrides[measureId];
    if (override) return override;
    
    // Return default action
    return conflictPreferences.defaultAction;
  };

  // Load preferences on mount and when auth token changes
  useEffect(() => {
    loadPreferences();
    
    // Listen for storage changes (login/logout events)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token') {
        if (e.newValue) {
          // User logged in, reload preferences
          loadPreferences();
        } else {
          // User logged out, reset to defaults
          setConflictPreferences(null);
          setDisplayPreferences(DEFAULT_DISPLAY_PREFERENCES);
        }
      }
    };
    
    // Also check periodically if token appeared (handles same-tab login)
    const checkToken = () => {
      const token = localStorage.getItem('auth_token');
      if (token && !conflictPreferences) {
        loadPreferences();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    const intervalId = setInterval(checkToken, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [conflictPreferences]);

  const value: PreferencesContextType = {
    conflictPreferences,
    displayPreferences,
    loading,
    loadPreferences,
    updatePreferences,
    updateDisplayPreferences,
    getPreferenceForMeasure,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}
